import { z } from 'zod';

/**
 * Montage-Einsätze, wie ein **Montage-Partner** sie im Portal sieht
 * (ADR 0085 Stufe 2, geöffnet mit ADR 0087).
 *
 * **Wichtige Änderung 2026-08-10 (Tech-Lead):** Die Sicht war zunächst auf
 * „wann, wohin, was, welcher Stand" verengt. Das war für einen Fremdbetrieb zu
 * wenig — er fährt zur Baustelle und muss dort arbeiten können, nicht raten.
 * Ein Montage-Partner sieht deshalb jetzt den **ganzen Einsatz**:
 *
 * * `notes` — die Einsatz-Anweisungen. Genau das, was er wissen muss.
 * * `projectCode` / `projectName` — wofür er da ist, und wie er eine Rückfrage
 *   zuordnen kann.
 * * `categoryName` — Art des Einsatzes (Montage, Wartung, Abnahme …).
 * * `crew` — wer sonst noch vor Ort ist. Man trifft sich ohnehin auf der
 *   Baustelle; für die Abstimmung **vorher** ist der Name das Nützlichste.
 *
 * **Die Folge muss man kennen:** `notes` ist ein internes Freitextfeld. Steht
 * dort eine kaufmännische Bemerkung, liest der Fremdbetrieb sie mit. Deshalb
 * markiert die interne Einsatz-Maske das Feld sichtbar, sobald ein Partner
 * zugeordnet ist. Ein zweites „Hinweise für den Partner"-Feld wäre die falsche
 * Antwort — es hieße, alles doppelt zu pflegen, und in der Praxis stünde die
 * wichtige Anweisung dann in dem Feld, das der Monteur nicht sieht.
 *
 * Draußen bleibt weiterhin, was den Einsatz nicht beschreibt: `partnerAccountId`
 * (kennt er selbst), `projectId` und `categoryId` (interne Schlüssel),
 * `technicianEmployeeIds` (Personalnummern unserer Leute — der Name reicht),
 * sowie alles Kaufmännische, das gar nicht an diesem Datensatz hängt.
 */
export const portalAssemblyTaskSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /** PLANNED | CONFIRMED | DONE | CANCELLED — der Stand, den wir führen. */
  status: z.string(),
  location: z.string().nullable(),
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  /** Einsatz-Anweisungen (internes Notizfeld, bewusst freigegeben). */
  notes: z.string().nullable(),
  /** Projektnummer — die Referenz für jede Rückfrage. */
  projectCode: z.string().nullable(),
  /** Projektname, zur Laufzeit aufgelöst (eine Umbenennung wirkt sofort). */
  projectName: z.string().nullable(),
  /** Art des Einsatzes, zur Laufzeit aufgelöst. */
  categoryName: z.string().nullable(),
  /**
   * Wer vor Ort ist: unsere Monteure (HR-Namen) und die Freitext-Zeile für
   * weitere/externe Kräfte, zu einer Zeile zusammengefasst. Der Partner braucht
   * keinen Einblick in unsere Personalstruktur — nur die Namen, mit denen er
   * sich abstimmt.
   */
  crew: z.string().nullable(),
});
export type PortalAssemblyTask = z.infer<typeof portalAssemblyTaskSchema>;

export const portalAssemblyResponseSchema = z.object({
  tasks: z.array(portalAssemblyTaskSchema),
});
export type PortalAssemblyResponse = z.infer<typeof portalAssemblyResponseSchema>;
